import { TimelineDto } from "models/dto/TimelineDto";
import { formatPhoneNumberWithExtentionAndCountryCode } from "shared/utils/utility";
import * as activityConstants from "shared/constants/activityConstants";
import { PhoneDto } from "models/dto/PhoneDto";
import { CarriersListResponse } from "models/dto/CarriersListResponse";
import { CarrierClassification } from "models/dto/CarrierClassification";
import {
  dateTimeLocale,
  displayTimeZone,
  parseUtcDate,
} from "shared/utils/dateTimeFormatting";

export const defaultValue = "N/A";

export const getValueOrDefault = (value: string | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return defaultValue;
  } else {
    return value;
  }
};

export const formatContentSummaryForCarrierRow = (activity: TimelineDto) => {
  const summaryEntityType =
    activityConstants.entityTypes.find(
      (type) => type.key === activity.entityType
    )?.text ?? "";

  const summaryAction =
    activityConstants.activityTypes.find(
      (type) => type.key === activity.actionType
    )?.text ?? "";

  const user = activity.userName ? `${activity.userName}: ` : "";

  return `${user} ${summaryAction} ${summaryEntityType}`;
};

export const formatPhoneForCarrierRow = (phone: PhoneDto) =>
  formatPhoneNumberWithExtentionAndCountryCode({
    phoneNumber: phone.phoneNumber ?? "",
    countryCode: phone.countryCode ?? "",
    extension: phone.extension ?? "",
  });

export const formatAddressForCarrierRow = (
  city: string,
  state: string
): string => [city, state].filter(Boolean).join(", ") || defaultValue;

export const formatDateForCarrierRow = (dateString: string) => {
  const parsedDate = parseUtcDate(dateString);

  if (isNaN(parsedDate.getTime())) {
    return "N/A";
  }

  return new Intl.DateTimeFormat(dateTimeLocale, {
    timeZone: displayTimeZone,
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).format(parsedDate);
};

export const getCarrierCellValues = (carrier: CarriersListResponse) => ({
  carrierName: getValueOrDefault(carrier.name),
  carrierOwner: getValueOrDefault(carrier.owner?.name),
  phone: carrier.phone
    ? formatPhoneForCarrierRow(carrier.phone) || defaultValue
    : defaultValue,
  email: getValueOrDefault(carrier.email),
  address: formatAddressForCarrierRow(carrier.city, carrier.state),
  country: getValueOrDefault(carrier.country),
  fleetSize:
    carrier.fleetSize == null ? defaultValue : carrier.fleetSize.toString(),
  shippeoOnboarded: carrier.shippeoOnboarded ? "Yes" : "No",
  lastActivityTime: carrier.lastActivity?.createdAt
    ? formatDateForCarrierRow(carrier.lastActivity.createdAt)
    : defaultValue,
  lastShipmentDelivered: carrier.lastShipmentDeliveredAt
    ? formatDateForCarrierRow(carrier.lastShipmentDeliveredAt)
    : defaultValue,
  lastActivitySummary: carrier.lastActivity
    ? formatContentSummaryForCarrierRow(carrier.lastActivity) || defaultValue
    : defaultValue,
  lastActivityNote: getValueOrDefault(carrier.lastActivity?.notes),
});

export const formatClassification = (
  classification: CarrierClassification | null
): string => {
  if (!classification) {
    return defaultValue;
  }

  const classificationToTextMap: Record<CarrierClassification, string> = {
    Freight: "Freight",
    Vehicle: "Vehicle",
    FreightAndVehicle: "Freight & Vehicle",
  };

  return classificationToTextMap[classification] ?? classification;
};
